/**
 * ERP Toast System — Global notification system
 * Replaces all alert()/window.confirm() with modern toasts
 * Position: bottom-center (taskbar area) so user sees it
 * 
 * GLOBAL COMPONENT — mount ONCE in App.js, use erpToast everywhere
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

let globalAddToast = null;

// Toast types
const ICONS = {
  success: { icon: CheckCircle, color: '#27ae60', bg: '#e8f8ef', border: '#a3d9b1' },
  error: { icon: XCircle, color: '#e74c3c', bg: '#fdeceb', border: '#f5c6cb' },
  warning: { icon: AlertTriangle, color: '#f39c12', bg: '#fef5e7', border: '#fdeab6' },
  info: { icon: Info, color: '#3498db', bg: '#ebf5fb', border: '#aed6f1' },
};

export const ERPToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type, duration }]);
    if (duration > 0) {
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
    }
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Expose globally
  globalAddToast = addToast;

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      {/* Toast Container — Bottom Center (taskbar area) */}
      <div style={{
        position: 'fixed', bottom: 36, left: '50%', transform: 'translateX(-50%)',
        zIndex: 1200, display: 'flex', flexDirection: 'column-reverse', gap: 8,
        maxWidth: 480, width: '90vw', pointerEvents: 'none'
      }} data-testid="erp-toast-container">
        {toasts.map(t => {
          const cfg = ICONS[t.type] || ICONS.info;
          const Icon = cfg.icon;
          return (
            <div key={t.id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderRadius: 8,
              background: cfg.bg, border: `1px solid ${cfg.border}`,
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              fontSize: 13, color: '#2d3748', fontFamily: 'Inter, sans-serif',
              animation: 'erpToastIn 200ms ease-out',
              pointerEvents: 'auto'
            }} data-testid={`erp-toast-${t.type}`}>
              <Icon size={18} style={{ color: cfg.color, flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{t.message}</span>
              <button onClick={() => removeToast(t.id)} style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#999'
              }}><X size={14} /></button>
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes erpToastIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </ToastContext.Provider>
  );
};

export const useERPToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useERPToast must be inside ERPToastProvider');
  return ctx;
};

// Global function — can be called from anywhere without hooks
export const erpToast = {
  success: (msg, dur) => globalAddToast?.(msg, 'success', dur),
  error: (msg, dur) => globalAddToast?.(msg, 'error', dur ?? 6000),
  warning: (msg, dur) => globalAddToast?.(msg, 'warning', dur),
  info: (msg, dur) => globalAddToast?.(msg, 'info', dur),
};

export default ERPToastProvider;
